/**
 * The Guests tab's one pipeline (#1013), mirrored from `services/event_journey.py`.
 *
 * A guest's column is DERIVED on the server from facts (invite sent, RSVP, attendance, follow-up stage);
 * the board response carries `journey_column` / `journey_label` and, in display order, `columns` (admin
 * renames applied). This module holds what the browser adds to that: tone, the guidance text under each
 * column, the actions each column offers by phase, the dialog effect lines and the move-form copy. The
 * keys here are the fixed milestone keys and the default follow-up keys; a custom follow-up stage an
 * admin adds arrives in `columns` and falls back to the generic follow-up look.
 */

export const TONES = {
    stone: { bg: '#F5F5F4', fg: '#57534E', bd: '#E7E5E4', dot: '#A8A29E' },
    blue: { bg: '#EFF6FF', fg: '#1D4ED8', bd: '#DBEAFE', dot: '#3B82F6' },
    emerald: { bg: '#ECFDF5', fg: '#047857', bd: '#D1FAE5', dot: '#10B981' },
    teal: { bg: '#F0FDFA', fg: '#0F766E', bd: '#CCFBF1', dot: '#14B8A6' },
    amber: { bg: '#FFFBEB', fg: '#B45309', bd: '#FDE68A', dot: '#F59E0B' },
    red: { bg: '#FEF2F2', fg: '#B91C1C', bd: '#FECACA', dot: '#EF4444' },
    violet: { bg: '#F5F3FF', fg: '#6D28D9', bd: '#DDD6FE', dot: '#8B5CF6' },
};

/** The six fixed milestone keys: renamable, never removable (D1). */
export const MILESTONE_KEYS = ['to_invite', 'invited', 'going', 'attended', 'no_show', 'closed'];
/** Milestones a move records as FACTS (`journey_target`); a follow-up stage key goes in `outreach_stage`. */
export const FACT_TARGETS = ['to_invite', 'invited', 'going', 'attended', 'no_show', 'closed'];
/** Columns that fill by themselves (the lightning bolt). */
export const AUTO_KEYS = ['invited', 'going', 'attended', 'no_show'];
/** The column a hand-off lands in; `handed_to_sales` is the pre-#1013 key for it. */
export const CONVERTED_KEYS = ['converted', 'handed_to_sales'];

/** Default columns in display order. The server's `columns` wins for labels and for custom stages. */
export const JOURNEY_COLUMNS = [
    { key: 'to_invite', label: 'To invite', tone: 'stone', auto: false, phase: 'before', kind: 'milestone', description: 'People you plan to invite. Send the invite from Nurture, or mark it sent if you invited them another way.' },
    { key: 'invited', label: 'Invited', tone: 'blue', auto: true, phase: 'before', kind: 'milestone', description: 'Invite sent, waiting for an answer. Nudge anyone quiet for a few days. A yes moves them to Going.', tip: 'Moves here when an invite is sent from Nurture, or when you mark one sent elsewhere' },
    { key: 'going', label: 'Going', tone: 'emerald', auto: true, phase: 'before', kind: 'milestone', description: 'Said yes and on the guest list. Send a reminder before the day. Checking in moves them to Attended.', tip: 'Moves here when they RSVP yes: registration form, reply, or recorded by hand' },
    { key: 'attended', label: 'Attended', tone: 'teal', auto: true, phase: 'after', kind: 'milestone', description: 'Checked in at the door, by face match or as a walk-in. Send a thank-you within a day.', tip: 'Moves here on check-in, an approved face match, or a walk-in' },
    { key: 'no_show', label: 'No-show', tone: 'red', auto: true, phase: 'after', kind: 'milestone', description: 'Said yes but didn’t come. Filled when the event ends. Reschedule them or follow up.', tip: 'Moves here when the event ends and a Going guest was not checked in' },
    { key: 'following_up', label: 'Following up', tone: 'blue', auto: false, phase: 'after', kind: 'follow_up', description: 'Thank-you or recap sent. Keep the conversation going until a meeting is booked.' },
    { key: 'meeting_booked', label: 'Meeting booked', tone: 'violet', auto: false, phase: 'after', kind: 'follow_up', description: 'A next conversation is on the calendar. Hand to sales once it’s qualified.' },
    { key: 'converted', label: 'Converted', tone: 'emerald', auto: false, phase: 'after', kind: 'follow_up', description: 'Handed to sales as a lead or deal. The event’s work with them is done.' },
    { key: 'closed', label: 'Closed', tone: 'stone', auto: false, phase: null, kind: 'closed', description: 'Not going further from this event: declined, no reply, not interested or rescheduled.' },
];

const DEFAULTS = Object.fromEntries(JOURNEY_COLUMNS.map((column, index) => [column.key, { ...column, order: index }]));
const CUSTOM_FOLLOW_UP = { tone: 'blue', auto: false, phase: 'after', kind: 'follow_up', description: 'A follow-up stage set up by your admin. Move guests here by hand.' };

/**
 * The columns to draw: the server's list (labels, admin-added stages) overlaid on the defaults. Without a
 * server list (an older response) the defaults are used.
 */
export function columnsFrom(serverColumns) {
    if (!Array.isArray(serverColumns) || serverColumns.length === 0) return JOURNEY_COLUMNS;
    return serverColumns.map((column) => {
        const base = DEFAULTS[column.key] || { key: column.key, ...CUSTOM_FOLLOW_UP };
        return {
            ...base,
            ...Object.fromEntries(Object.entries(column).filter(([, value]) => value !== undefined && value !== null && value !== '')),
            tone: base.tone,
            tip: base.tip,
            kind: column.kind || base.kind,
        };
    });
}

/** The guidance text under a column header (OWN-11), falling back to the default for the key. */
export function describeColumn(key, columns = JOURNEY_COLUMNS) {
    const found = (columns || []).find((column) => column.key === key);
    return found?.description || DEFAULTS[key]?.description || '';
}

export const columnLabel = (key, columns = JOURNEY_COLUMNS) => (columns || []).find((column) => column.key === key)?.label
    || DEFAULTS[key]?.label || String(key || '').replace(/[_-]+/g, ' ');

export const toneOf = (key, columns = JOURNEY_COLUMNS) => TONES[(columns || []).find((column) => column.key === key)?.tone || DEFAULTS[key]?.tone || 'blue'];

// ---- phase -------------------------------------------------------------------------------------

/**
 * `before | event_day | after` (#1013): the event's own `started_at` / `ended_at` stamps decide it. An event
 * with neither is "before" however its date reads, so the bar offers Start and Reschedule and Check-in stays
 * locked (the server's date-rule `phase` made today's unstarted event "Live" with no Start button). Only a
 * legacy event whose date is already past (the server says `after`) reads as ended, and Reopen opens it.
 */
export function phaseOf(event) {
    if (event?.ended_at) return 'after';
    if (event?.started_at) return 'event_day';
    return event?.phase === 'after' ? 'after' : 'before';
}

// ---- derivation (a fallback only: the server's `journey_column` wins) ---------------------------

export const CLOSE_REASON_LABELS = {
    declined: 'Declined',
    cancelled: 'Cancelled',
    not_interested: 'Not interested',
    no_reply: 'No reply',
    never_invited: 'Never invited',
    wrong_person: 'Wrong person',
    rescheduled: 'Rescheduled',
    closed: 'Closed',
};

export const closeReasonLabel = (reason, rescheduledTo) => {
    if (!reason) return 'Closed';
    if (reason === 'rescheduled' && rescheduledTo) return `Rescheduled → ${rescheduledTo}`;
    return CLOSE_REASON_LABELS[reason] || String(reason).replace(/[_-]+/g, ' ');
};

/** What the dialog offers when closing by hand (the P2 values a person can pick). */
export const CLOSE_REASON_OPTIONS = ['declined', 'cancelled', 'not_interested', 'no_reply', 'never_invited', 'wrong_person']
    .map((value) => ({ value, label: CLOSE_REASON_LABELS[value] }));

const isFollowUpKey = (key, columns) => (columns || JOURNEY_COLUMNS).some((column) => column.key === key && column.kind === 'follow_up');

/** The same order as `event_journey.column`; used only when a card arrives without `journey_column`. */
export function deriveColumn(card, columns = JOURNEY_COLUMNS) {
    if (card?.journey_column) return card.journey_column;
    const stage = card?.outreach_stage;
    if (stage && isFollowUpKey(stage, columns)) return stage;
    if (stage === 'closed') return 'closed';
    if (card?.attendance_status === 'attended') return 'attended';
    if (card?.attendance_status === 'no_show') return 'no_show';
    if (card?.rsvp_status === 'declined' || card?.rsvp_status === 'cancelled') return 'closed';
    if (card?.rsvp_status === 'confirmed') return 'going';
    if (card?.invited_at) return 'invited';
    return 'to_invite';
}

/** Where a guest came from, in words (the drawer's Source fact): a form names itself, a code path never shows (#919-06). */
const SOURCE_LABELS = {
    lead_capture: 'Registration form', walk_in: 'Walk-in', manual: 'Added by hand', event_outreach: 'Event outreach',
    face_match: 'Face match', csv_import: 'CSV import', import: 'CSV import',
};
export function sourceLabel(card) {
    if (card?.registration_form_id) return `Form · ${card.registration_form_name || 'registration form'}`;
    const source = card?.source;
    if (!source) return '—';
    return SOURCE_LABELS[source] || String(source).replace(/[_-]+/g, ' ');
}

export const isWalkIn = (card) => Boolean(card?.is_walk_in) || card?.source === 'walk_in';

/** The reason a closed card shows ("Declined", "Rescheduled → …"); derived from RSVP when none is stored. */
export function closedReason(card) {
    if (card?.close_reason) return closeReasonLabel(card.close_reason, card.rescheduled_to_label);
    if (card?.rsvp_status === 'declined') return 'Declined';
    if (card?.rsvp_status === 'cancelled') return 'Cancelled';
    return 'Closed';
}

/** The "why is this guest here" line in the drawer. */
export function whyText(card, key) {
    switch (key) {
        case 'to_invite': return 'Not contacted yet';
        case 'invited': return card?.reconfirm_requested_at ? 'Asked to reconfirm, no answer yet' : 'Invite sent, no RSVP yet';
        case 'going': return 'Because RSVP is Yes';
        case 'attended': return isWalkIn(card) ? 'Because they walked in and were checked in' : 'Because they were checked in';
        case 'no_show': return 'Because they were going and never checked in';
        case 'following_up': return 'Moved by hand after the thank-you';
        case 'meeting_booked': return 'Moved by hand';
        case 'converted': return 'Handed to sales';
        case 'closed': return closedReason(card);
        default: return 'Moved by hand';
    }
}

// ---- actions (the issue's table) ---------------------------------------------------------------

/**
 * Card actions by column and phase. `kind` is a quick action (`invite`, `nudge`, `remind`, `checkin`,
 * `thanks`, `resched`, `deal`) or `move:<column>`. At most two per card, never a Call (OWN-12).
 */
export const COLUMN_ACTIONS = {
    to_invite: {
        before: [{ label: 'Invite', icon: 'mail', kind: 'invite', tip: 'Sends the invite email; the card moves to Invited' }, { label: 'Mark invited', icon: 'check', kind: 'move:invited' }],
        event_day: [{ label: 'Invite', icon: 'mail', kind: 'invite', tip: 'Sends the invite email; the card moves to Invited' }, { label: 'Mark invited', icon: 'check', kind: 'move:invited' }],
        after: [{ label: 'Reschedule', icon: 'calendar', kind: 'resched' }, { label: 'Close', icon: 'archive', kind: 'move:closed' }],
    },
    invited: {
        before: [{ label: 'Nudge', icon: 'bell-ring', kind: 'nudge' }, { label: 'RSVP yes', icon: 'user-check', kind: 'move:going' }],
        event_day: [{ label: 'Check in', icon: 'badge-check', kind: 'checkin', tip: 'They came without an RSVP' }],
        after: [{ label: 'Reschedule', icon: 'calendar', kind: 'resched' }, { label: 'Close', icon: 'archive', kind: 'move:closed' }],
    },
    going: {
        before: [{ label: 'Remind', icon: 'bell', kind: 'remind' }, { label: 'Can’t make it', icon: 'user-x', kind: 'move:closed', closeReason: 'declined' }],
        event_day: [{ label: 'Check in', icon: 'badge-check', kind: 'checkin' }],
        after: [{ label: 'Check in', icon: 'badge-check', kind: 'checkin' }],
    },
    attended: {
        before: [],
        event_day: [{ label: 'Send thank-you', icon: 'mail', kind: 'thanks', tip: 'Sends the thank-you; the card moves to Following up' }],
        after: [{ label: 'Send thank-you', icon: 'mail', kind: 'thanks', tip: 'Sends the thank-you; the card moves to Following up' }],
    },
    no_show: {
        before: [],
        event_day: [{ label: 'Reschedule', icon: 'calendar', kind: 'resched', tip: 'Add them to a future event' }, { label: 'Follow up', icon: 'arrow-right', kind: 'move:following_up' }],
        after: [{ label: 'Reschedule', icon: 'calendar', kind: 'resched', tip: 'Add them to a future event' }, { label: 'Follow up', icon: 'arrow-right', kind: 'move:following_up' }],
    },
    following_up: {
        all: [{ label: 'Book meeting', icon: 'calendar-check', kind: 'move:meeting_booked' }, { label: 'Not interested', icon: 'archive', kind: 'move:closed', closeReason: 'not_interested' }],
    },
    meeting_booked: { all: [{ label: 'Hand to sales', icon: 'briefcase', kind: 'move:converted' }] },
    converted: { all: [{ label: 'Open deal', icon: 'external-link', kind: 'deal' }] },
    closed: { all: [{ label: 'Reschedule', icon: 'calendar', kind: 'resched', tip: 'Add them to a future event' }] },
};

/** Closed guests who are not offered Reschedule: they said no, or already have a new date. */
const NO_RESCHEDULE_REASONS = ['not_interested', 'wrong_person', 'rescheduled'];

/** The actions a card shows now. A custom follow-up stage offers none (its moves are drag, menu and drawer). */
export function actionsFor(card, columnKey, phase) {
    const table = COLUMN_ACTIONS[columnKey];
    if (!table) return [];
    if (columnKey === 'closed' && (NO_RESCHEDULE_REASONS.includes(card?.close_reason) || card?.rescheduled_to_event_id)) return [];
    return (table[phase] || table.all || []).slice(0, 2);
}

/** Whether a move into `key` is blocked before the event starts (attendance opens at Start). */
export const BLOCKED_BEFORE_START = ['attended', 'no_show'];
export const START_FIRST_TEXT = 'Start the event first · attendance opens when it starts';

// ---- the move dialog ---------------------------------------------------------------------------

export const MOVE_TITLES = {
    to_invite: 'Move back to To invite',
    invited: 'Mark invited',
    going: 'Record RSVP: going',
    attended: 'Check in by hand',
    no_show: 'Mark no-show',
    following_up: 'Move to Following up',
    meeting_booked: 'Meeting booked',
    converted: 'Hand to sales',
    closed: 'Close for this event',
};

export const moveTitle = (key, columns) => MOVE_TITLES[key] || `Move to ${columnLabel(key, columns)}`;

/** What a move records, said before it is saved (the dialog's effect line). Mirrors `event_journey.effect_of`. */
export function effectOf(fromKey, toKey, columns = JOURNEY_COLUMNS) {
    const order = (key) => (columns || JOURNEY_COLUMNS).findIndex((column) => column.key === key);
    if (toKey === 'going') return { tone: 'emerald', text: 'Records the RSVP as Yes. They join the guest list, check-in and the Going count.' };
    if (toKey === 'invited' && fromKey === 'to_invite') return { tone: 'blue', text: 'Records an invite sent outside Nurture and sets a nudge reminder.' };
    if (toKey === 'attended') return { tone: 'teal', text: 'Records a manual check-in. Attended and show rate update with it.' };
    if (toKey === 'no_show') return { tone: 'red', text: 'Records attendance as no-show.' };
    if (toKey === 'closed') return { tone: 'stone', text: 'Takes them out of this event. Declined or Cancelled also records that as their RSVP; otherwise RSVP and attendance stay as recorded.' };
    if (['going', 'attended', 'no_show'].includes(fromKey) && order(toKey) < order(fromKey)) {
        return { tone: 'amber', text: fromKey === 'going' ? 'Resets the RSVP to No response.' : 'Undoes the attendance record.' };
    }
    return null;
}

// ---- next step chip ----------------------------------------------------------------------------

const FOLLOW_UP_PREFIX = { invited: 'Nudge due', attended: 'Thank-you due', following_up: 'Follow-up', meeting_booked: 'Meeting' };

/**
 * The due chip on a card: `{text, tone}` or null. The server's follow-up (`follow_up_due_at`,
 * `follow_up_state`) wins; otherwise the phase explains what is waiting (the prototype's `dueOf`).
 * `formatDate` turns the server date into a label.
 */
export function dueOf(card, columnKey, phase, formatDate = (value) => String(value)) {
    if (card?.follow_up_due_at) {
        const overdue = card.follow_up_state === 'overdue';
        const prefix = FOLLOW_UP_PREFIX[columnKey] || 'Next step';
        return { text: `${prefix}${overdue ? ' overdue' : ''} · ${formatDate(card.follow_up_due_at, card.follow_up_due_date_only)}`, tone: overdue ? 'red' : card.follow_up_state === 'due' ? 'amber' : 'stone' };
    }
    if (columnKey === 'invited' && card?.reconfirm_requested_at) return { text: 'Reconfirm for the new date', tone: 'amber' };
    if (phase === 'after') {
        if (columnKey === 'attended') return { text: 'Thank-you due', tone: 'amber' };
        if (columnKey === 'no_show') return { text: 'Reschedule or follow up', tone: 'amber' };
        if (columnKey === 'invited') return { text: 'Event over · never replied', tone: 'amber' };
        if (columnKey === 'to_invite') return { text: 'Event over · never invited', tone: 'stone' };
    }
    return null;
}

/** Sort rank: overdue first, then due, then scheduled, then none. */
export const dueRank = (due) => (!due ? 3 : due.tone === 'red' ? 0 : due.tone === 'amber' ? 1 : 2);

// ---- check-in -----------------------------------------------------------------------------------

/** Check-in lists Going, Attended and (after the event) No-show guests; a late arrival is switched on. */
export const CHECK_IN_COLUMNS = ['going', 'attended', 'no_show'];

export const CHECK_IN_LOCKED_TEXT = 'Check-in opens when the event starts. This is the list staff will see at the door.';
